import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Editor } from 'primereact/editor';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { PickList } from 'primereact/picklist';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useState } from 'react';

import { useSite } from '../../../context/site';
import EmployeeDropdown from '../../employee-dropdown';
import ParentSearch from '../../parent/search';
import ProgramSearch from '../../program/search';
import ServiceTypeIndividualDropdown from '../../service-type-ind-dropdown';
import { GetEditorFormats } from '../../../shared';

function ServiceIndividualUpdate({ open, data, onClose }) {
    const { GetChildrenByParent, IsServiceTypeJ2TC, UpdateServiceIndividual } = useSite();

    const [dateService, setDateService] = useState(new Date());
    const [employeeId, setEmployeeId] = useState('');
    const [parent, setParent] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [sourceChildrenPrograms, setSourceChildrenPrograms] = useState([]);
    const [childrenPrograms, setChildrenPrograms] = useState([]);
    const [familyNumber, setFamilyNumber] = useState(0);
    const [notes, setNotes] = useState('');

    const [openSearchParent, setOpenSearchParent] = useState(false);
    const [openSearchProgram, setOpenSearchProgram] = useState(false);

    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');

    const buttonDisabled = () => {
        if (dateService.toLocaleDateString() !== new Date(data.dateService).toLocaleDateString()) {
            return false;
        }

        if (employeeId !== data.employee._id) {
            return false;
        }

        if (parent._id !== data.parent._id) {
            return false;
        }

        if (serviceType !== data.serviceType._id) {
            return false;
        }

        if (IsServiceTypeJ2TC(serviceType) && `${familyNumber}` !== `${data.familyNumber}`) {
            return false;
        }

        if (notes !== data.notes) {
            return false;
        }

        let deleted = false;

        for (const dcp of data.childrenPrograms) {
            const cp = childrenPrograms.find((c) => c.child._id === dcp.child._id);

            if (!cp) {
                deleted = true;
                break;
            }
        }

        if (deleted) {
            return false;
        }

        let created = false;

        for (const cp of childrenPrograms) {
            const dcp = data.childrenPrograms.find((c) => c.child._id === cp.child._id);

            if (!dcp) {
                created = true;
                break;
            }
        }

        if (created) {
            return false;
        }

        let updated = false;

        for (const cp of childrenPrograms) {
            const dcp = data.childrenPrograms.find((c) => c.child._id === cp.child._id);
            const cpProgram = cp?.program?._id;
            const dcpProgram = dcp?.program?._id;

            if (cpProgram !== dcpProgram) {
                updated = true;
                break;
            }
        }

        if (updated) {
            return false;
        }

        return true;
    };

    const onCancel = () => {
        onClose({ status: 499, message: 'Canceled' });
    };

    const onUpdate = () => {
        setProcessing(true);

        const updateData = {
            _id: data._id,
            dateService,
            employee: employeeId,
            parent: parent._id,
            serviceType,
            childrenPrograms: childrenPrograms.map((cp) => {
                const res = {
                    child: cp.child._id
                };

                if (cp.program?._id) {
                    res.program = cp.program._id;
                }

                return res;
            }),
            familyNumber,
            notes
        };

        UpdateServiceIndividual(updateData, (res) => {
            if (res.status !== 200) {
                setProcessingError(res.message);
                setProcessing(false);
            } else {
                onClose(res);
            }
        });
    };

    const onChange = (event) => {
        setSourceChildrenPrograms(event.source);
        setChildrenPrograms(event.target);
    };

    const FooterContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Update"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onUpdate}
            />
        </div>
    );

    const sourceItemTemplate = (item) => {
        return (
            <div className="flex align-items-center">
                <div className={item.child.active ? '' : 'text-red-500'}>
                    {item.child.nameFirst} {item.child.nameLast}
                </div>
            </div>
        );
    };

    const targetItemTemplate = (item) => {
        return (
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    {item.child.nameFirst} {item.child.nameLast}
                </div>

                {!item.program?._id ? (
                    <div className="flex-auto">
                        <Button
                            rounded
                            size="small"
                            icon="pi pi-plus"
                            severity="help"
                            onClick={() => {
                                setOpenSearchProgram(true);
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <div className="flex-auto">{item.program.programName}</div>

                        <div className="flex-auto">
                            <Button
                                className="ml-1"
                                rounded
                                icon="pi pi-minus"
                                severity="danger"
                                size="small"
                                onClick={() => {
                                    setChildrenPrograms((prev) => {
                                        return [
                                            ...prev.map((c) => {
                                                if (c.child._id === item.child._id) {
                                                    return {
                                                        child: { ...c.child },
                                                        program: ''
                                                    };
                                                }

                                                return c;
                                            })
                                        ];
                                    });
                                }}
                            />
                        </div>
                    </>
                )}

                {!openSearchProgram ? null : (
                    <ProgramSearch
                        open={openSearchProgram}
                        onClose={(res) => {
                            setOpenSearchProgram(false);

                            if (res?._id) {
                                setChildrenPrograms((prev) => {
                                    return [
                                        ...prev.map((c) => {
                                            if (c.child._id === item.child._id) {
                                                return {
                                                    child: { ...c.child },
                                                    program: { ...res }
                                                };
                                            }

                                            return c;
                                        })
                                    ];
                                });
                            }
                        }}
                    />
                )}
            </div>
        );
    };

    useEffect(() => {
        setDateService(new Date(data.dateService));
        setEmployeeId(data.employee._id);
        setParent(data.parent);
        setServiceType(data.serviceType._id);
        setChildrenPrograms(data.childrenPrograms);
        setFamilyNumber(data.familyNumber);
        setNotes(data.notes);

        GetChildrenByParent({ parentId: data.parent._id }, (res) => {
            if (res.status === 200) {
                setSourceChildrenPrograms(
                    res.data.children
                        .filter((c) => {
                            const x = data.childrenPrograms.find((y) => c._id === y.child._id);

                            return !x;
                        })
                        .map((c) => {
                            return {
                                child: c,
                                program: ''
                            };
                        })
                );
            }
        });
    }, [data, GetChildrenByParent]);

    return (
        <Dialog
            header="Update Individual Service"
            visible={open}
            onHide={onCancel}
            footer={FooterContent}
            className="w-9"
        >
            <ErrorMessage
                error={processingError}
                onClick={() => {
                    setProcessingError('');
                }}
            />

            <ProcessingMessage processing={processing} />

            <div className="mt-2" style={{ display: processing ? 'none' : 'block' }}>
                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="dateServiceId" className="font-bold block mb-2">
                            Service Date
                        </label>

                        <Calendar
                            inputId="dateServiceId"
                            value={dateService}
                            onChange={(e) => setDateService(e.value)}
                            showIcon
                            showButtonBar
                            appendTo="self"
                        />
                    </div>

                    <div className="flex-auto">
                        <label htmlFor="employeeId" className="font-bold block mb-2">
                            Employee
                        </label>

                        <EmployeeDropdown
                            employeeId={employeeId}
                            onChange={(employeeId) => setEmployeeId(employeeId)}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="serviceTypeId" className="font-bold block mb-2">
                            Service Type
                        </label>

                        <ServiceTypeIndividualDropdown
                            serviceTypeIndividualId={serviceType}
                            onChange={(serviceTypeId) => {
                                setServiceType(serviceTypeId);
                            }}
                        />
                    </div>
                </div>

                {!IsServiceTypeJ2TC(serviceType) ? null : (
                    <div className="card flex flex-wrap gap-3 p-fluid">
                        <div className="flex-auto">
                            <label htmlFor="familyNumberId" className="font-bold block mb-2">
                                Family Number
                            </label>

                            <InputText
                                id="familyNumberId"
                                value={familyNumber}
                                keyfilter={/^[0-9]*$/}
                                maxLength={5}
                                onChange={(e) => {
                                    setFamilyNumber(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                )}

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="parentId" className="font-bold block mb-2">
                            Parent
                        </label>

                        <p onClick={() => setOpenSearchParent(true)}>
                            {parent.nameFirst}
                            <span> </span>
                            {parent.nameLast}
                        </p>
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="parentId" className="font-bold block mb-2">
                            Children
                        </label>

                        <PickList
                            source={sourceChildrenPrograms}
                            target={childrenPrograms}
                            onChange={onChange}
                            sourceItemTemplate={sourceItemTemplate}
                            showSourceControls={false}
                            targetItemTemplate={targetItemTemplate}
                            showTargetControls={false}
                            sourceHeader={`Children for ${parent.nameFirst}`}
                            targetHeader={`Selected children for ${parent.nameFirst}`}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="notesId" className="font-bold block mb-2">
                            Notes
                        </label>

                        <Editor
                            id="notesId"
                            headerTemplate={<GetEditorFormats />}
                            value={notes}
                            onTextChange={(e) => setNotes(e.htmlValue)}
                            style={{ height: '320px' }}
                        />
                    </div>
                </div>
            </div>

            {!openSearchParent ? null : (
                <ParentSearch
                    open={openSearchParent}
                    onClose={(res) => {
                        setOpenSearchParent(false);

                        if (res?._id) {
                            setSourceChildrenPrograms([
                                ...res.children.map((c) => {
                                    return {
                                        child: { ...c },
                                        program: ''
                                    };
                                })
                            ]);
                            setParent(res);
                            setChildrenPrograms([]);
                        }
                    }}
                />
            )}
        </Dialog>
    );
}

function ErrorMessage({ error, onClick }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} onClick={onClick} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return (
        <>
            <p>Udating individual service...</p>

            <ProgressSpinner />
        </>
    );
}

export default ServiceIndividualUpdate;
